import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  ArrowLeft, 
  Save, 
  X,
  Calendar,
  Gift,
  Copy,
  Search,
  Filter,
  RefreshCw,
  LogOut,
  Percent,
  DollarSign,
  Users,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  query,
  orderBy 
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const CouponsManagement = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'percentage', // percentage or fixed
    value: 0,
    minOrderAmount: 0,
    maxDiscountAmount: 0,
    usageLimit: 0, // 0 means unlimited
    usedCount: 0,
    startDate: '',
    endDate: '',
    isActive: true
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching coupons from Firestore...');
      const couponsQuery = query(collection(db, 'coupons'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(couponsQuery);
      const couponsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('🎫 Fetched coupons:', couponsData.length);
      setCoupons(couponsData);
    } catch (error) {
      console.error('❌ Error fetching coupons:', error);
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  // Optimized filtering with useMemo
  const filteredCoupons = useMemo(() => {
    console.log('🔍 Filtering coupons:', {
      totalCoupons: coupons.length,
      searchQuery,
      statusFilter
    });
    
    const filtered = coupons.filter(coupon => {
      const matchesSearch = coupon.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           coupon.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      let matchesStatus = true;
      if (statusFilter === 'active') {
        matchesStatus = coupon.isActive === true;
      } else if (statusFilter === 'inactive') {
        matchesStatus = coupon.isActive === false;
      } else if (statusFilter === 'expired') {
        const now = new Date();
        const endDate = coupon.endDate ? new Date(coupon.endDate) : null;
        matchesStatus = endDate && endDate < now;
      }
      
      return matchesSearch && matchesStatus;
    });
    
    console.log('✅ Filtered result:', filtered.length, 'coupons');
    return filtered;
  }, [coupons, searchQuery, statusFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredCoupons.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCoupons = filteredCoupons.slice(startIndex, endIndex);

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const generateCouponCode = useCallback(() => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, code: result }));
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Logout failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🎫 Form submission started...');
    console.log('Form data:', formData);
    
    if (!formData.code || !formData.description || formData.value <= 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check if coupon code already exists
    if (!editingCoupon) {
      const existingCoupon = coupons.find(coupon => 
        coupon.code.toLowerCase() === formData.code.toLowerCase()
      );
      if (existingCoupon) {
        toast.error('Coupon code already exists');
        return;
      }
    }

    try {
      const couponData = {
        ...formData,
        createdAt: editingCoupon ? coupons.find(c => c.id === editingCoupon.id)?.createdAt : new Date(),
        updatedAt: new Date()
      };

      console.log('Coupon data to save:', couponData);
      
      if (editingCoupon) {
        console.log('Updating existing coupon:', editingCoupon.id);
        await updateDoc(doc(db, 'coupons', editingCoupon.id), couponData);
        toast.success('Coupon updated successfully!');
      } else {
        console.log('Adding new coupon to Firestore...');
        const docRef = await addDoc(collection(db, 'coupons'), couponData);
        console.log('New coupon added with ID:', docRef.id);
        toast.success('Coupon added successfully!');
      }

      console.log('🔄 Calling fetchCoupons...');
      fetchCoupons();
      console.log('🚪 Calling resetForm...');
      resetForm();
    } catch (error) {
      console.error('❌ Error saving coupon:', error);
      toast.error(`Failed to save coupon: ${error.message}`);
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description,
      type: coupon.type,
      value: coupon.value,
      minOrderAmount: coupon.minOrderAmount || 0,
      maxDiscountAmount: coupon.maxDiscountAmount || 0,
      usageLimit: coupon.usageLimit || 0,
      usedCount: coupon.usedCount || 0,
      startDate: coupon.startDate || '',
      endDate: coupon.endDate || '',
      isActive: coupon.isActive !== false
    });
    setShowAddForm(true);
  };

  const handleDelete = async (couponId) => {
    if (window.confirm('Are you sure you want to delete this coupon?')) {
      try {
        await deleteDoc(doc(db, 'coupons', couponId));
        toast.success('Coupon deleted successfully!');
        fetchCoupons();
      } catch (error) {
        console.error('Error deleting coupon:', error);
        toast.error('Failed to delete coupon');
      }
    }
  };

  const copyCouponCode = useCallback((code) => {
    navigator.clipboard.writeText(code);
    toast.success('Coupon code copied to clipboard!');
  }, []);

  const resetForm = useCallback(() => {
    console.log('🔄 Resetting form...');
    setFormData({
      code: '',
      description: '',
      type: 'percentage',
      value: 0,
      minOrderAmount: 0,
      maxDiscountAmount: 0,
      usageLimit: 0,
      usedCount: 0,
      startDate: '',
      endDate: '',
      isActive: true
    });
    setShowAddForm(false);
    setEditingCoupon(null);
  }, []);

  const CouponCard = React.memo(({ coupon }) => {
    const getStatusColor = (coupon) => {
      if (!coupon.isActive) return '#6b7280'; // gray
      
      const now = new Date();
      const endDate = coupon.endDate ? new Date(coupon.endDate) : null;
      
      if (endDate && endDate < now) return '#dc2626'; // red - expired
      return '#059669'; // green - active
    };

    const getStatusText = (coupon) => {
      if (!coupon.isActive) return 'Inactive';
      
      const now = new Date();
      const endDate = coupon.endDate ? new Date(coupon.endDate) : null;
      
      if (endDate && endDate < now) return 'Expired';
      return 'Active';
    };

    const getStatusIcon = (coupon) => {
      if (!coupon.isActive) return <XCircle style={{ width: '12px', height: '12px' }} />;
      
      const now = new Date();
      const endDate = coupon.endDate ? new Date(coupon.endDate) : null;
      
      if (endDate && endDate < now) return <XCircle style={{ width: '12px', height: '12px' }} />;
      return <CheckCircle style={{ width: '12px', height: '12px' }} />;
    };

    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        border: '1px solid #e5e7eb',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
      }}>
        {/* Status Badge */}
        <div style={{
          position: 'absolute',
          top: '16px',
          right: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          backgroundColor: `${getStatusColor(coupon)}20`,
          color: getStatusColor(coupon),
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '600'
        }}>
          {getStatusIcon(coupon)}
          {getStatusText(coupon)}
        </div>

        {/* Coupon Code */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
          paddingRight: '100px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            backgroundColor: '#ff6b35',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(255,107,53,0.3)'
          }}>
            <Gift style={{ width: '24px', height: '24px', color: 'white' }} />
          </div>
          <div>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#1f2937',
              margin: '0 0 4px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {coupon.code}
              <button
                onClick={() => copyCouponCode(coupon.code)}
                style={{
                  padding: '4px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              >
                <Copy style={{ width: '14px', height: '14px', color: '#6b7280' }} />
              </button>
            </h3>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: 0,
              lineHeight: '1.4'
            }}>
              {coupon.description}
            </p>
          </div>
        </div>

        {/* Coupon Details */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div>
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              marginBottom: '4px',
              fontWeight: '600'
            }}>
              DISCOUNT TYPE
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              {coupon.type === 'percentage' ? (
                <Percent style={{ width: '14px', height: '14px', color: '#ff6b35' }} />
              ) : (
                <DollarSign style={{ width: '14px', height: '14px', color: '#ff6b35' }} />
              )}
              {coupon.type === 'percentage' ? `${coupon.value}%` : `₹${coupon.value}`}
            </div>
          </div>

          <div>
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              marginBottom: '4px',
              fontWeight: '600'
            }}>
              MINIMUM ORDER
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              {coupon.minOrderAmount > 0 ? `₹${coupon.minOrderAmount}` : 'No minimum'}
            </div>
          </div>

          {coupon.maxDiscountAmount > 0 && (
            <div>
              <div style={{
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '4px',
                fontWeight: '600'
              }}>
                MAX DISCOUNT
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                ₹{coupon.maxDiscountAmount}
              </div>
            </div>
          )}

          {coupon.usageLimit > 0 && (
            <div>
              <div style={{
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '4px',
                fontWeight: '600'
              }}>
                USAGE LIMIT
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#1f2937',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <Users style={{ width: '14px', height: '14px', color: '#ff6b35' }} />
                {coupon.usedCount || 0} / {coupon.usageLimit}
              </div>
            </div>
          )}
        </div>

        {/* Validity Period */}
        {(coupon.startDate || coupon.endDate) && (
          <div style={{
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '12px',
              color: '#6b7280',
              marginBottom: '6px',
              fontWeight: '600'
            }}>
              VALIDITY PERIOD
            </div>
            <div style={{
              fontSize: '13px',
              color: '#374151',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Calendar style={{ width: '14px', height: '14px', color: '#6b7280' }} />
              <span>
                {coupon.startDate && new Date(coupon.startDate).toLocaleDateString()}
                {coupon.startDate && coupon.endDate && ' - '}
                {coupon.endDate && new Date(coupon.endDate).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px'
        }}>
          <button
            onClick={() => handleEdit(coupon)}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
          >
            <Edit style={{ width: '16px', height: '16px' }} />
            Edit
          </button>
          <button
            onClick={() => handleDelete(coupon.id)}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fecaca'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
          >
            <Trash2 style={{ width: '16px', height: '16px' }} />
            Delete
          </button>
        </div>
      </div>
    );
  });

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #ff6b35',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ fontSize: '16px', color: '#6b7280' }}>Loading coupons...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        padding: '16px 0',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button
                onClick={() => navigate('/admin')}
                style={{
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ArrowLeft style={{ width: '20px', height: '20px' }} />
              </button>
              <div style={{ width: '48px', height: '48px', background: 'linear-gradient(45deg, #ff6b35, #ff3f3f)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(255,107,53,0.3)' }}>
                <span style={{ color: 'white', fontWeight: '900', fontSize: '1.5rem' }}>B</span>
              </div>
              <div>
                <h1 style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1f2937', margin: 0 }}>Coupons Management</h1>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>Manage promotional coupons</p>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Button
                onClick={fetchCoupons}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <RefreshCw style={{ width: '16px', height: '16px' }} />
                Refresh
              </Button>
              <Button
                onClick={() => {
                  setEditingCoupon(null);
                  setShowAddForm(true);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#ff6b35',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 12px rgba(255,107,53,0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e55a2b'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff6b35'}
              >
                <Plus style={{ width: '20px', height: '20px' }} />
                Add Coupon
              </Button>
              <Button
                onClick={handleLogout}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <LogOut style={{ width: '16px', height: '16px' }} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Search and Filter */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: '1', minWidth: '300px' }}>
              <Search style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                color: '#6b7280'
              }} />
              <Input
                type="text"
                placeholder="Search coupons by code or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 44px',
                  borderRadius: '12px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: 'white',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Filter style={{ width: '20px', height: '20px', color: '#6b7280' }} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  outline: 'none',
                  backgroundColor: 'white',
                  transition: 'border-color 0.2s ease',
                  minWidth: '150px'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                <option value="all">All Coupons</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            
            <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>
              Showing {filteredCoupons.length} of {coupons.length} coupons
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            marginBottom: '32px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#1f2937',
                margin: 0
              }}>
                {editingCoupon ? 'Edit Coupon' : 'Add New Coupon'}
              </h2>
              <button
                onClick={resetForm}
                style={{
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  color: '#6b7280',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X style={{ width: '20px', height: '20px' }} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Coupon Code *
                  </Label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <Input
                      type="text"
                      placeholder="Enter coupon code"
                      value={formData.code}
                      onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                      required
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: 'white',
                        transition: 'border-color 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                    {!editingCoupon && (
                      <Button
                        type="button"
                        onClick={generateCouponCode}
                        style={{
                          padding: '12px 16px',
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Generate
                      </Button>
                    )}
                  </div>
                </div>
                
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Discount Type *
                  </Label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'white',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
              </div>

              <div>
                <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                  Description *
                </Label>
                <textarea
                  placeholder="Enter coupon description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  required
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: 'white',
                    transition: 'border-color 0.2s ease',
                    resize: 'vertical'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Discount Value *
                  </Label>
                  <Input
                    type="number"
                    placeholder={formData.type === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                    value={formData.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                    required
                    min="0"
                    step={formData.type === 'percentage' ? '0.1' : '1'}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'white',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Minimum Order Amount
                  </Label>
                  <Input
                    type="number"
                    placeholder="Enter minimum order amount"
                    value={formData.minOrderAmount}
                    onChange={(e) => setFormData(prev => ({ ...prev, minOrderAmount: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'white',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Maximum Discount Amount
                  </Label>
                  <Input
                    type="number"
                    placeholder="Enter maximum discount amount"
                    value={formData.maxDiscountAmount}
                    onChange={(e) => setFormData(prev => ({ ...prev, maxDiscountAmount: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'white',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Usage Limit
                  </Label>
                  <Input
                    type="number"
                    placeholder="0 = unlimited"
                    value={formData.usageLimit}
                    onChange={(e) => setFormData(prev => ({ ...prev, usageLimit: parseInt(e.target.value) || 0 }))}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'white',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'white',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
                
                <div>
                  <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #e5e7eb',
                      fontSize: '14px',
                      outline: 'none',
                      backgroundColor: 'white',
                      transition: 'border-color 0.2s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '2px solid #e5e7eb' }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Active Coupon
                  </span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button
                  type="button"
                  onClick={resetForm}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 12px rgba(255,107,53,0.3)'
                  }}
                >
                  <Save style={{ width: '16px', height: '16px' }} />
                  {editingCoupon ? 'Update Coupon' : 'Add Coupon'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Coupons Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '20px'
        }}>
          {filteredCoupons.length === 0 ? (
            <div style={{
              gridColumn: '1 / -1',
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '48px',
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: '#f3f4f6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                fontSize: '32px'
              }}>
                🎫
              </div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#1f2937',
                margin: '0 0 8px 0'
              }}>
                {searchQuery ? 'No coupons found' : 'No coupons yet'}
              </h3>
              <p style={{
                fontSize: '1rem',
                color: '#6b7280',
                margin: '0 0 24px 0'
              }}>
                {searchQuery ? 'Try adjusting your search criteria' : 'Start by creating your first coupon'}
              </p>
              {!searchQuery && (
                <Button
                  onClick={() => {
                    setEditingCoupon(null);
                    setShowAddForm(true);
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ff6b35',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    margin: '0 auto',
                    boxShadow: '0 4px 12px rgba(255,107,53,0.3)'
                  }}
                >
                  <Plus style={{ width: '20px', height: '20px' }} />
                  Add First Coupon
                </Button>
              )}
            </div>
          ) : (
            <>
              {currentCoupons.map((coupon) => (
                <CouponCard key={coupon.id} coupon={coupon} />
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{
                  gridColumn: '1 / -1',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '16px',
                  marginTop: '32px',
                  padding: '24px',
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '12px 20px',
                      backgroundColor: currentPage === 1 ? '#f3f4f6' : '#ff6b35',
                      color: currentPage === 1 ? '#9ca3af' : 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Previous
                  </button>

                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    padding: '12px 20px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px'
                  }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '12px 20px',
                      backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#ff6b35',
                      color: currentPage === totalPages ? '#9ca3af' : 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CouponsManagement;
